import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  asapScheduler,
  BehaviorSubject,
  forkJoin,
  Observable,
  observeOn,
  of,
  throwError,
} from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AvailableTypesTrackColors } from '@common/components/create-object/tabs/object-sensors-tab/create-sensors.service';
import {
  AvailableDateTimePeriod,
  AvailableRecentlyDateTimePeriodTypes,
  DateTimePeriodParamsForm,
} from '@common/components/date-time-period/date-time-period.component';
import { AccessService, getKeysFromBinary } from '@common/services/access.service';
import { PaginatorService } from '@common/services/paginator.service';
import { defaultColor } from '@common/utils/colors';
import { transformTuiToLuxon } from '@common/utils/date-form-transform';
import { formatDefaultDateFromTui } from '@common/utils/format-date';
import { GraphRoadEventTypes } from '@services/chart.service';
import { PortalService } from '@services/portal.service';
import { MailingReportItem, MailingReportsList } from '@store/mailingReports/mailingReports.state';
import { deleteTracksFromReportsPage } from '@store/reports/reports.actions';
import {
  RawReportTemplateItem,
  ReportTemplateItem,
  ReportTemplatesList,
} from '@store/reports/reports.state';

export interface ReportBuildContext {
  reportTaskId: string;
  objectIds: number[];
  objectGroupIds: number[];
  templateId: number;
  datetimeStart: string;
  datetimeEnd: string;
}

export interface ReportTrackSettingForm {
  selectedColorType: FormControl<AvailableTypesTrackColors>;
  selectedColorSingleTrack: FormControl<string>;
  trackLineWeight: FormControl<number>;
  detectorTrips: FormControl<boolean>;
  buildTrack: FormControl<boolean>;
  markers: FormGroup<{
    isStop: FormControl<boolean>;
    isParking: FormControl<boolean>;
    isGasStation: FormControl<boolean>;
    isPlum: FormControl<boolean>;
    isSpeedLimit: FormControl<boolean>;
    isStartAndEndPosition: FormControl<boolean>;
  }>;
}

export type LoadedReportElement = Record<string, LoadedReportTable | LoadedReportChart>;
export interface LoadedReportTable {
  paginatorService: PaginatorService;
  columns: ReportTableColumns[];
  data: any[];
  tableId: string;
  count: number;
  type: string;
  summary: any;
  viewType: 'table';
  tableName: string;
  isNoWrapTable: boolean;
  isNumbering: boolean;
  isVertical: boolean;
  trackShortInfo: {
    withTrips: boolean;
  } | null;
}

export interface LoadedReportChart {
  xAxis: string[];
  yAxis: YAxisChart[];
  options: any;
  chartId: string;
  name: string;
  viewType?: 'chart';
  isGroupReport: boolean;
  markers: ChartRoadEventsMarker | null;
  chartRangeDates?: {
    chartRangeStartFirst: string;
    chartRangeEndFirst: string;
  };
  chartRangeZoom?: {
    chartRangeStart: string;
    chartRangeEnd: string;
  };
  dataZoomValues?: {
    startValue: any;
    endValue: any;
  };
}

export interface MarkerInfo {
  lat: string;
  lng: string;
  endDate: string;
  startDate: string;
}

export type ChartRoadEventsMarker = Record<GraphRoadEventTypes, MarkerInfo[]>;

export interface YAxisChart {
  data: any[];
  id: string;
  name: string;
}

export interface ReportTableColumns {
  name: string;
  title: string;
}

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  private readonly httpClient = inject(HttpClient);
  private readonly portalService = inject(PortalService);
  private readonly accessService = inject(AccessService);
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  private readonly translate = inject(TranslateService);

  public isLoadingReportList = new BehaviorSubject(false);

  public isLoadingContentOnMap = new BehaviorSubject<any>(false);

  public reportBuildContext$: BehaviorSubject<ReportBuildContext | null> =
    new BehaviorSubject<ReportBuildContext | null>(null);

  public selectedReport$ = new BehaviorSubject<any>(null);

  loadedReportElements: LoadedReportElement = {};
  currenReportTaskId: string | null = null;

  public displayedReportElementsIds: string[] = [];

  get hasDisplayedElements(): boolean {
    return this.displayedReportElementsIds.length > 0;
  }

  public changeLegends: any = new BehaviorSubject(null);

  /**
   * Легенды графика публикует GraphReportComponent при инициализации, то есть посреди change
   * detection, а читает их ReportsComponent — соседнее поддерево, которое к этому моменту уже
   * проверено, и пометить его грязным изнутри графика нельзя. Поэтому граница между поддеревьями
   * асинхронная: применяем легенды в следующем тике (микротаска, кадр не теряется).
   */
  readonly chartLegends$: Observable<any> = this.changeLegends.pipe(observeOn(asapScheduler));

  formGroup: FormGroup = new FormGroup({
    selectedTemplateControl: new FormControl(null, Validators.required),
    // selectedObject: new FormControl(null, Validators.required),

    objArray: this.fb.array<any>([]),
    // periodDateFrom: new FormControl(null, Validators.required), // only ISO time
    // periodDateTo: new FormControl(null, Validators.required), // only ISO time
    dateTimePeriod: new FormGroup<DateTimePeriodParamsForm>({
      periodDateStart: new FormControl(null, [Validators.required]),
      periodDateEnd: new FormControl(null, [Validators.required]),
      periodValue: new FormControl(AvailableDateTimePeriod.Today),
      recentlyPeriod: new FormControl(1),
      recentlyPeriodType: new FormControl(AvailableRecentlyDateTimePeriodTypes.Days),
      currentPeriod: new FormControl(false),
    }),
    trackSetting: new FormGroup<ReportTrackSettingForm>({
      buildTrack: new FormControl<boolean>(true, { nonNullable: true }),
      selectedColorType: new FormControl<AvailableTypesTrackColors>(
        AvailableTypesTrackColors.Trip,
        {
          nonNullable: true,
        },
      ),
      selectedColorSingleTrack: new FormControl<string>(defaultColor, { nonNullable: true }),
      trackLineWeight: new FormControl<number>(5, {
        nonNullable: true,
      }),
      detectorTrips: new FormControl<boolean>(true, { nonNullable: true }),
      markers: new FormGroup({
        isStop: new FormControl<boolean>(false, { nonNullable: true }),
        isParking: new FormControl<boolean>(false, { nonNullable: true }),
        isGasStation: new FormControl<boolean>(false, { nonNullable: true }),
        isPlum: new FormControl<boolean>(false, { nonNullable: true }),
        isSpeedLimit: new FormControl<boolean>(false, { nonNullable: true }),
        isStartAndEndPosition: new FormControl<boolean>(false, { nonNullable: true }),
      }),
    }),
  });

  resetForm() {
    this.formGroup.reset();
    (this.formGroup.get('objArray') as FormArray).clear();
  }

  clearReportSubjects() {
    // this.displayedReportElementsIds = [];
    this.portalService.isVisibleReportResult.next(false);
    this.reportBuildContext$.next(null);
    // this.loadedReportElements = {};
  }

  clearSaves() {
    this.displayedReportElementsIds = [];
    this.loadedReportElements = {};
    this.currenReportTaskId = null;
    this.selectedReport$.next(null);
    this.store.dispatch(deleteTracksFromReportsPage());
  }

  get periodString() {
    const controls: any = (
      this.formGroup.get('dateTimePeriod') as FormGroup<DateTimePeriodParamsForm>
    ).controls;

    if (!controls['periodDateStart'] || !controls['periodDateEnd']) {
      return '-';
    }

    return formatDefaultDateFromTui(
      controls['periodDateStart'].value,
      controls['periodDateEnd'].value,
      {
        startPlaceholder: this.translate.instant('CHOOSE_START_DATE'),
        endPlaceholder: this.translate.instant('CHOOSE_END_DATE'),
      },
    );
  }

  createReport(form: any): Observable<ReportTemplateItem> {
    return this.httpClient.post<RawReportTemplateItem>('reports/templates/', form).pipe(
      map((report) => {
        return {
          ...report,
          currentUserAccess:
            typeof report.currentUserAccess === 'number'
              ? (getKeysFromBinary(
                  this.accessService.allAccessRights.report_templates,
                  report.currentUserAccess,
                ) as ('view' | 'edit' | 'delete')[])
              : report.currentUserAccess,
        };
      }),
    );
  }

  updateReport(form: any, reportId: number): Observable<ReportTemplateItem> {
    return this.httpClient.put<RawReportTemplateItem>(`reports/templates/${reportId}/`, form).pipe(
      map((report) => {
        return {
          ...report,
          currentUserAccess:
            typeof report.currentUserAccess === 'number'
              ? (getKeysFromBinary(
                  this.accessService.allAccessRights.report_templates,
                  report.currentUserAccess,
                ) as ('view' | 'edit' | 'delete')[])
              : report.currentUserAccess,
        };
      }),
    );
  }

  // getReportTemplateById(reportId: any, card: boolean = false) {
  //   let params = new HttpParams();
  //
  //   if (card) {
  //     params = params.set('card', card);
  //   }
  //   return this.httpClient.get(`reports/templates/${reportId}/`, { params });
  // }

  getReportTemplateById(reportId: any, card = false) {
    let params = new HttpParams();
    if (card) {
      params = params.set('card', card);
    }

    const mainRequest$ = this.httpClient
      .get<any>(`reports/templates/${reportId}/`, { params })
      .pipe(
        map((template) => {
          return {
            ...template,
            currentUserAccess:
              typeof template.currentUserAccess === 'number'
                ? getKeysFromBinary(
                    this.accessService.allAccessRights.report_templates,
                    template.currentUserAccess,
                  )
                : template.currentUserAccess,
          };
        }),
      );

    if (card) {
      return mainRequest$;
    }

    const geozonesSettings$ = this.getGeozonesSettingsReportTemplate(reportId);

    return forkJoin({
      template: mainRequest$,
      geozonesSettings: geozonesSettings$,
    }).pipe(
      map(({ template, geozonesSettings }) => ({
        ...template,
        settings: {
          ...template.settings,
          addressSettings: {
            ...template.settings?.addressSettings,
            ...geozonesSettings,
          },
        },
      })),
    );
  }

  getGeozonesSettingsReportTemplate(reportId: number) {
    if (!this.accessService.checkUserCommonAccess(['access_geozones'])) {
      return of({});
    } else {
      return this.httpClient.get(`reports/templates/${reportId}/settings/geozones/`);
    }
  }

  listReport(params: any = null): Observable<ReportTemplatesList> {
    let httpParams = new HttpParams();
    if (params?.accountId) {
      httpParams = httpParams.set('account_id', params.accountId.toString());
    }
    return this.httpClient
      .get<RawReportTemplateItem[]>('reports/templates/', { params: httpParams })
      .pipe(
        map((res) => {
          return res.map((report) => {
            return {
              ...report,
              currentUserAccess:
                typeof report.currentUserAccess === 'number'
                  ? (getKeysFromBinary(
                      this.accessService.allAccessRights.report_templates,
                      report?.currentUserAccess,
                    ) as ('view' | 'edit' | 'delete')[])
                  : report.currentUserAccess,

              trackSettings: report?.trackSettings
                ? report.trackSettings
                : {
                    buildTrack: true,
                    colorMode: AvailableTypesTrackColors.Single,
                    color: 'black',
                    lineThickness: 5,
                    detectorTrips: true,
                    markers: {
                      isStartAndEndPosition: false,
                      isParking: false,
                      isStop: false,
                      isSpeedLimit: false,
                      isGasStation: false,
                      isPlum: false,
                    },
                  },
            };
          });
        }),
      );
  }

  deleteReport(reportId: number) {
    return this.httpClient.delete(`reports/templates/${reportId}/`);
  }

  executeReport(body: any) {
    return this.httpClient.post(`reports/`, body).pipe(
      map((report: any) => {
        const { chartMarkers = undefined, markerSettings = undefined } = report;

        return {
          ...report,
          chartMarkers: {
            withConnectionLosses:
              chartMarkers?.isConnectionLosses ||
              markerSettings?.isConnectionLosses?.value ||
              false,
            withEngineHours:
              chartMarkers?.isEngineHours || markerSettings?.isEngineHours?.value || false,
            withOverSpeed:
              chartMarkers?.isSpeedLimit || markerSettings?.isSpeedLimit?.value || false,
            withParkings: chartMarkers?.isParking || markerSettings?.isParking?.value || false,
            withPlums: chartMarkers?.isPlum || markerSettings?.isPlum?.value || false,
            withRefuels: chartMarkers?.isGasStation || markerSettings?.isGasStation?.value || false,
            withStops: chartMarkers?.isStop || markerSettings?.isStop?.value || false,
            withTrips: chartMarkers?.isTrips || markerSettings?.isTrips?.value || false,
          },
        };
      }),
    );
  }

  cancelReportGeneration(taskId: string) {
    return this.httpClient.post(`reports/cancel/`, { taskId });
  }

  reportBuildStatus(id: any) {
    return this.httpClient.get(`reports/status/?reportTaskId=${id}`);
  }

  getReportByTaskId(taskId: string) {
    return this.httpClient.get<{ report: any }>(`reports/${taskId}/`);
  }

  getTableById(id: any, page: number, perPage: number) {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());
    return this.httpClient.get(`reports/table/${id}/`, { params });
  }

  getChartById(id: any) {
    return this.httpClient.get<LoadedReportChart>(`reports/chart/${id}/`);
  }

  getSeriesDataChart(data: {
    seriesId?: string;
    seriesIds?: string[];
    objectChartId?: string;
    objectChartIds?: string[];
    timestampEnd?: string;
    timestampStart?: string;
  }) {
    const body: any = {
      maxPoints: 1000,
    };

    if (data.objectChartIds && data.objectChartIds.length > 0) {
      body.objectChartIds = data.objectChartIds;
    } else if (data.objectChartId) {
      body.objectChartId = data.objectChartId;
    }

    if (data.seriesIds && data.seriesIds.length > 0) {
      body.seriesIds = data.seriesIds;
    } else if (data.seriesId) {
      body.seriesId = data.seriesId;
    }

    if (data.timestampStart) {
      body.timestampStart = data.timestampStart;
    }
    if (data.timestampEnd) {
      body.timestampEnd = data.timestampEnd;
    }

    return this.httpClient.post<any>(`reports/special_data/`, body);
  }

  exportAsFormat(reportId: any, format: 'xlsx' | 'csv' | 'pdf') {
    return this.httpClient
      .get(`reports/export/${format}/${reportId}`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.error instanceof Blob) {
            return new Observable((observer) => {
              const reader = new FileReader();
              reader.onload = () => {
                observer.error(error);
              };
              reader.onerror = () => {
                observer.error(error);
              };
              reader.readAsText(error.error);
            });
          }
          return throwError(() => error);
        }),
      );
  }

  mailingReportsList(): Observable<MailingReportsList> {
    return this.httpClient
      .get<{ mailingReports: MailingReportsList }>('reports/mailing/')
      .pipe(map((response) => response.mailingReports));
  }

  getMailingReportById(mailingId: number): Observable<MailingReportItem> {
    return this.httpClient
      .get<{ mailingReport: MailingReportItem }>(`reports/mailing/${mailingId}/`)
      .pipe(map((response) => response.mailingReport));
  }

  createMailingReport(form: any): Observable<MailingReportItem> {
    return this.httpClient
      .post<{ mailingReport: MailingReportItem }>('reports/mailing/', form)
      .pipe(map((response) => response.mailingReport));
  }

  updateMailingReport(mailingId: number, form: any): Observable<MailingReportItem> {
    return this.httpClient
      .put<{ mailingReport: MailingReportItem }>(`reports/mailing/${mailingId}/`, form)
      .pipe(map((response) => response.mailingReport));
  }

  updateToggleActiveMailingReport(mailingId: number, state: boolean) {
    return this.httpClient.post(`reports/mailing/${mailingId}/activate/`, { state });
  }

  deleteMailingReport(id: number) {
    return this.httpClient.delete(`reports/mailing/${id}/`);
  }

  getChartRangeDates(): {
    chartRangeStartFirst: string;
    chartRangeEndFirst: string;
  } {
    const dateTimePeriodGroup = this.formGroup.get(
      'dateTimePeriod',
    ) as FormGroup<DateTimePeriodParamsForm>;
    const controls = dateTimePeriodGroup.controls;

    const periodDateStart = controls.periodDateStart.value;
    const periodDateEnd = controls.periodDateEnd.value;

    if (!periodDateStart?.[0] || !periodDateEnd?.[0]) {
      return { chartRangeStartFirst: '', chartRangeEndFirst: '' };
    }

    const startDateTime = transformTuiToLuxon(periodDateStart[0], {
      time: periodDateStart[1],
    });

    const endDateTime = transformTuiToLuxon(periodDateEnd[0], {
      time: periodDateEnd[1],
    });

    return {
      chartRangeStartFirst: (startDateTime.toISO({ includeOffset: false }) || '') + 'Z',
      chartRangeEndFirst: (endDateTime.toISO({ includeOffset: false }) || '') + 'Z',
    };
  }
}
